import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Users, Plus, Trash2, MoreHorizontal } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { useData } from '../context/DataContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

const COLORS = [
  '#8B5CF6', '#EC4899', '#F59E0B', '#10B981', 
  '#3B82F6', '#EF4444', '#6366F1', '#14B8A6'
];

const ClientsPage = () => {
  const { clients, createClient, deleteClient } = useData();
  const navigate = useNavigate();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [newClient, setNewClient] = useState({ name: '', description: '', color: '#8B5CF6' });
  const [isLoading, setIsLoading] = useState(false);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newClient.name.trim()) return;
    
    setIsLoading(true);
    try {
      await createClient(newClient);
      setNewClient({ name: '', description: '', color: '#8B5CF6' });
      setIsDialogOpen(false);
      toast.success('Client created');
    } catch (err) {
      toast.error('Failed to create client');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (clientId) => {
    if (window.confirm('Delete this client? Ads will not be deleted.')) {
      try {
        await deleteClient(clientId);
        toast.success('Client deleted');
      } catch (err) {
        toast.error('Failed to delete');
      }
    }
  };

  return (
    <div className="min-h-screen" data-testid="clients-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <Users className="w-7 h-7 text-violet-400" />
            <h1 className="text-2xl md:text-3xl font-bold text-white">Clients</h1>
          </div>
          <p className="text-zinc-500">Organize ads by client</p>
        </div>

        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-violet-600 hover:bg-violet-500" data-testid="add-client-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Client
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-zinc-900 border-zinc-800">
            <DialogHeader>
              <DialogTitle className="text-white">Add Client</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4 mt-4">
              <div>
                <Label className="text-zinc-400">Name</Label>
                <Input
                  data-testid="client-name-input"
                  value={newClient.name}
                  onChange={(e) => setNewClient({ ...newClient, name: e.target.value })}
                  placeholder="Client name"
                  className="mt-1.5 bg-zinc-800 border-zinc-700 text-white"
                  required
                />
              </div>
              <div>
                <Label className="text-zinc-400">Description (optional)</Label>
                <Input
                  value={newClient.description}
                  onChange={(e) => setNewClient({ ...newClient, description: e.target.value })}
                  placeholder="Brief description"
                  className="mt-1.5 bg-zinc-800 border-zinc-700 text-white"
                />
              </div>
              <div>
                <Label className="text-zinc-400">Color</Label>
                <div className="flex gap-2 mt-2">
                  {COLORS.map((color) => (
                    <button
                      key={color}
                      type="button"
                      onClick={() => setNewClient({ ...newClient, color })}
                      className={`w-8 h-8 rounded-full transition-transform ${
                        newClient.color === color ? 'ring-2 ring-white ring-offset-2 ring-offset-zinc-900 scale-110' : ''
                      }`}
                      style={{ backgroundColor: color }}
                    />
                  ))}
                </div>
              </div>
              <Button
                type="submit"
                disabled={isLoading}
                className="w-full bg-violet-600 hover:bg-violet-500"
                data-testid="create-client-btn"
              >
                {isLoading ? 'Creating...' : 'Create Client'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Clients Grid */}
      {clients.length === 0 ? (
        <div className="text-center py-20">
          <div className="w-20 h-20 rounded-full bg-zinc-800/50 flex items-center justify-center mx-auto mb-4">
            <Users className="w-10 h-10 text-zinc-600" />
          </div>
          <h3 className="text-xl font-semibold text-zinc-300 mb-2">No clients yet</h3>
          <p className="text-zinc-500 mb-6">Create your first client to organize ads</p>
          <Button
            onClick={() => setIsDialogOpen(true)}
            className="bg-violet-600 hover:bg-violet-500"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Client
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {clients.map((client, index) => (
            <motion.div
              key={client.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              className="
                group bg-zinc-900/50 border border-white/5 rounded-xl p-5
                hover:border-violet-500/30 transition-colors cursor-pointer
              "
              onClick={() => navigate(`/?client=${client.id}`)}
              data-testid={`client-card-${client.id}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div
                    className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold"
                    style={{ backgroundColor: client.color }}
                  >
                    {client.name.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <h3 className="font-medium text-zinc-200">{client.name}</h3>
                    <p className="text-sm text-zinc-500">
                      {client.ad_count} {client.ad_count === 1 ? 'ad' : 'ads'}
                    </p>
                  </div>
                </div>
                
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="opacity-0 group-hover:opacity-100 text-zinc-500 hover:text-white"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <MoreHorizontal className="w-4 h-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="bg-zinc-900 border-zinc-800">
                    <DropdownMenuItem
                      className="text-red-400 focus:text-red-300 focus:bg-zinc-800"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(client.id);
                      }}
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
              
              {client.description && (
                <p className="mt-3 text-sm text-zinc-500 line-clamp-2">
                  {client.description}
                </p>
              )}
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ClientsPage;
